<template>
  <div>
    <!-- Só depois do dono: no múltiplo direto os ids do filtro moram nele, e montar
         antes filtraria por lista vazia, mostrando um quadro vazio. -->
    <ontos-relationship-board v-if="owner && property"
                              :owner-context="ownerContext"
                              :owner="owner"
                              :property="property"
                              :reverse-info="reverseInfo"
                              :domains="relDomains"
                              :workspace="workspace"
                              :reload-owner="reloadOwner"
                              @update:owner="owner = $event"
                              @open-instance="onOpenInstance"
                              @no-dimensions="goToOwner"
                              @showMessage="$emit('showMessage', $event)"
                              @showErrorMessage="$emit('showErrorMessage', $event)">
      <template #title>
        <ontos-page-header :title="property.name">
          <!-- A coleção antes do identificador: sozinho ele não diz de que tipo de
               instância estes itens são. -->
          <template #subtitle>
            <span class="owner-idea text-grey">{{ ownerIdeaName }}</span>
            <span class="owner-identifier">{{ ownerLabel }}</span>
          </template>
        </ontos-page-header>
      </template>
      <!-- Voltar ao dono é controle da janela, fora do bloco das ações do quadro -->
      <template #controls>
        <ontos-header-action icon="mdi-arrow-left" :label="ownerIdeaName" :to="ownerRoute"/>
      </template>
    </ontos-relationship-board>

    <ontos-view-instance-dialog v-if="instanceOnView"
                                :context="instanceOnView"
                                :preview="previewOnView"
                                :workspace="workspace"
                                @changed="load()"
                                @deleted="load()"
                                @closed="instanceOnView = null"
                                @showMessage="$emit('showMessage', $event)"
                                @showErrorMessage="$emit('showErrorMessage', $event)"/>
  </div>
</template>

<script lang="ts">
import {Component, toNative} from 'vue-facing-decorator'
import IdeaLevelRouteComponent from "@/components/shared/IdeaLevelRouteComponent";
import RelationshipBoard from "@/components/live/instance/RelationshipBoard.vue";
import ViewInstanceDialog from "@/components/live/instance/ViewInstanceDialog.vue";
import IdeaProperty from "@/types/IdeaProperty";
import {InstanceContext} from "@/types/InstanceContext";
import {instanceRoute} from "@/utils/instanceLink";
import {Preview} from "@/utils/instancePreview";

/**
 * Página do Kanban de UM relacionamento de UMA instância — a tela cheia do bloco da ficha, e
 * uma URL que dá para guardar e compartilhar. A rota da coleção não serve para isso: ela
 * mostraria todos os itens, não só os desta instância.
 *
 * O tabuleiro é o mesmo KanbanBoard da página de coleção; aqui só se resolve de quem são os
 * itens — e eles saem da leitura do dono, sem uma segunda requisição.
 *
 * Editar um item pela ficha que abre por cima RECARREGA O DONO, e não remonta o quadro: os itens
 * do quadro são os do `_rels` do dono, então reler é o que os atualiza. Remontar por `:key`
 * jogaria fora a posição do quadro para readotar a mesma lista velha.
 */
@Component({
  emits: ['crumb'],
  components: {
    'ontos-relationship-board': RelationshipBoard,
    'ontos-view-instance-dialog': ViewInstanceDialog
  },
})
class RelationshipKanban extends IdeaLevelRouteComponent {
  /** A instância dona. Precisa vir do servidor: os ids do múltiplo direto moram nela. */
  owner: any = null
  reverseInfo: any = null
  /**
   * Os candidatos de cada relacionamento do dono, que esta leitura já traz (`rel-domains`): é deles
   * que o "Vincular existente" do quadro escolhe, e com eles que confere quem roubaria de outro dono.
   */
  relDomains: any = {}
  instanceOnView: InstanceContext | null = null
  previewOnView: Preview | null = null

  /** O que o quadro já tem do item vai junto: a ficha o mostra já, e a edição (o lápis) abre já. */
  onOpenInstance(context: InstanceContext, preview: Preview | null) {
    this.instanceOnView = context
    this.previewOnView = preview
  }

  get ownerContext(): InstanceContext {
    const {system, idea, instance} = this.params
    return {system, idea, instance}
  }

  get property(): IdeaProperty | undefined {
    return this.idea.properties.find(p => p.alias === this.params.property)
  }

  get ownerIdeaName(): string {
    return this.idea.name
  }

  get ownerLabel(): string {
    return this.owner ? this.owner._identifier : ''
  }

  get ownerRoute() {
    return instanceRoute(this.ownerContext)
  }

  /**
   * Recarregar o dono é só recarregar esta tela. Vai como função porque o vínculo
   * pós-criação precisa AGUARDÁ-LA; ver a prop no RelationshipBoard.
   *
   * Método, e não campo com arrow: viaja como prop, e o Vue 3 entrega os métodos já ligados à
   * instância — num campo, o `this` capturado seria o da instância crua da classe, e o `owner`
   * recarregado não chegaria à tela. É a mesma pedra do `countTitle` no tabuleiro.
   */
  async reloadOwner() {
    //Só o dono: o filtro é computado e o tabuleiro se recarrega sozinho logo em seguida,
    //então remontar aqui seria trabalho dobrado.
    await this.load()
  }

  async load() {
    const {system: systemAlias, idea: ideaAlias, instance} = this.ownerContext
    const res = await this.$instances.get(
        {systemAlias, ideaAlias, instanceId: Number(instance)},
        {
          //Os itens do quadro saem DESTA leitura, e não de uma segunda: `relProperties` traz
          //cada canção inteira, `depth: 2` traz os relacionamentos dela — sem os quais a raia
          //sairia com o id no lugar do nome — e os carimbos alimentam o prazo das raias finais.
          meta: ["rels", "identifier", "instance", "state", "stateChangedAt", "createdAt", "updatedAt"],
          options: ["relProperties", "rel-domains"],
          depth: 2
        })
    this.owner = res.value
    //O degrau é a instância DONA, que é o que a rota endereça. Identidade junto do
    //texto: o App compara com a rota (ver instanceLabel no App.vue).
    this.$emit('crumb', {
      system: systemAlias, idea: ideaAlias, instance, text: this.ownerLabel
    })
    this.reverseInfo = res.reverseInfo
    this.relDomains = res.relDomains || {}
    //URL inventada ou propriedade que saiu do design: volta para a instância
    if (!this.property || !this.property.relationship) return this.goToOwner()
  }

  async goToOwner() {
    await this.$router.push(this.ownerRoute).catch(() => undefined)
  }
}

export default toNative(RelationshipKanban)
</script>

<style scoped>
/* Só o nome da coleção é apagado, por ser rótulo: o identificador é o assunto e
   fica com a cor normal do texto. */
.owner-idea {
  margin-right: 6px;
}

.owner-identifier {
  font-weight: 500;
}
</style>
